/* import __COLOCATED_TEMPLATE__ from './conversation-sla-applied-by-rule.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type Session from 'embercom/services/session';
import type IntlService from 'embercom/services/intl';
import { truncateEventText, isEventTextTruncated } from 'embercom/lib/inbox2/event-text-truncation';

interface Signature {
  Args: any;
}

export default class ConversationSlaAppliedByRuleComponent extends Component<Signature> {
  @service declare session: Session;
  @service declare intl: IntlService;

  get ruleSlaTKey(): string {
    return 'inbox.conversation-sla-applied-by-rule.rule-sla-changed-v2';
  }

  get customBotSlaTKey(): string {
    return 'inbox.conversation-sla-applied-by-rule.custom-bot-sla-changed-v2';
  }

  get renderableData() {
    return this.args.part.renderableData;
  }

  get ruleLabel(): string {
    return this.renderableData.rule?.label ?? '';
  }

  get slaLabel(): string {
    return this.renderableData.conversationSla?.label ?? '';
  }

  get isRuleLabelTruncated(): boolean {
    return isEventTextTruncated(this.ruleLabel);
  }

  get isSlaLabelTruncated(): boolean {
    return isEventTextTruncated(this.slaLabel);
  }

  get displayRuleLabel(): string {
    return truncateEventText(this.ruleLabel);
  }

  get displaySlaLabel(): string {
    return truncateEventText(this.slaLabel);
  }

  get customBotTitle(): string {
    return this.renderableData.customBotSummary?.title ?? '';
  }

  get isCustomBotTitleTruncated(): boolean {
    return isEventTextTruncated(this.customBotTitle);
  }

  get displayCustomBotTitle(): string {
    return truncateEventText(this.customBotTitle);
  }

  /**
   * Returns the SLA part of the event (e.g., 'SLA: "{sla}" ').
   * Used when both SLA and rule/title are truncated so each can have its own IcTooltip.
   */
  get slaPart(): string {
    return this.intl.t('inbox.conversation-sla-applied-by-rule.sla-part-v2', {
      sla: truncateEventText(this.slaLabel),
    });
  }

  /**
   * Returns the rule part of the event (e.g., 'applied by rule "{rule}"').
   * Used when both SLA and rule are truncated so each can have its own IcTooltip.
   */
  get rulePart(): string {
    return this.intl.t('inbox.conversation-sla-applied-by-rule.rule-part-v2', {
      rule: truncateEventText(this.ruleLabel),
    });
  }

  /**
   * Returns the custom bot part of the event (e.g., 'applied by Workflow "{title}"').
   * Used when both SLA and title are truncated so each can have its own IcTooltip.
   */
  get customBotPart(): string {
    return this.intl.t('inbox.conversation-sla-applied-by-rule.custom-bot-part-v2', {
      title: truncateEventText(this.customBotTitle),
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::EventParts::ConversationSlaAppliedByRule': typeof ConversationSlaAppliedByRuleComponent;
    'inbox2/conversation-stream/event-parts/conversation-sla-applied-by-rule': typeof ConversationSlaAppliedByRuleComponent;
  }
}
